import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {
  ContactDetailsErrorBannerState,
  ContactDetailsErrorTypesEnum,
} from '../../../../store/panels/app/banners/bannersState';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerCloseAction} from '../../../common/banners/bannerCloseAction';
import {BannerContent} from '../../../common/banners/bannerContent';
import {BannerContentTitle} from '../../../common/banners/bannerContentTitle';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';
import {buildErrorDisplayedTrackingPayload} from '../../../contacts/contactDetailsTrackingHelpers';
import {ContactSourceDisplayName} from '../../../contacts/contactSourceDisplayName';

/*
 * Constants.
 */

const ErrorIcon: IconName = 'contactsFilledSmall';

/*
 * Props.
 */

interface ContactDetailsErrorBannerProps {
  onDismissClick: Handler;
  onMergePreview: Handler;
  openContactDetailsPopUp: Handler;
  state: ContactDetailsErrorBannerState;
}

/*
 * Style.
 */

const StyledButtonsContainerDiv = styled.div`
  display: flex;
  margin-top: 15px;
`;

const StyledButton = styled(InlineLinkButton)`
  text-transform: uppercase;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.bold};
  margin-right: 12px;
`;

/*
 * Component.
 */

export const ContactDetailsErrorBanner: FC<ContactDetailsErrorBannerProps> = (props) => {
  const theme = useUserTheme();
  const {onDismissClick, onMergePreview, openContactDetailsPopUp, state} = props;

  // Track the error if we have a card and a payload
  if (state.params?.mainCard) {
    const trackingPayload = buildErrorDisplayedTrackingPayload(
      state.contactDetailsErrorType,
      state.params.mainCard,
    );
    if (trackingPayload) {
      trackInterfaceEvent(InterfaceEventsEnum.P4_CONTACT_DETAILS_ERROR_DISPLAYED, trackingPayload);
    }
  }

  switch (state.contactDetailsErrorType) {
    case ContactDetailsErrorTypesEnum.MERGE_FAILED:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName={ErrorIcon} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="contact-merge-failed-message">Contact merge failed.</Trans>
            </BannerContentTitle>
            {getTryAgainLocalized()}
          </BannerContent>
        </Banner>
      );
    case ContactDetailsErrorTypesEnum.MERGE_PREVIEW_FAILED:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName={ErrorIcon} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="contact-merge-preview-failed-message">Contact merge preview failed.</Trans>
            </BannerContentTitle>
            {getTryAgainLocalized()}
          </BannerContent>
        </Banner>
      );
    case ContactDetailsErrorTypesEnum.CREATION_FAILED:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName={ErrorIcon} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="contact-creation-failed-message">Contact creation failed.</Trans>
            </BannerContentTitle>
            {getTryAgainLocalized()}
          </BannerContent>
        </Banner>
      );
    case ContactDetailsErrorTypesEnum.DELETION_FAILED:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName={ErrorIcon} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="contact-deletion-failed-message">Contact deletion failed.</Trans>
            </BannerContentTitle>
            {getTryAgainLocalized()}
          </BannerContent>
        </Banner>
      );
    case ContactDetailsErrorTypesEnum.CONVERT_PRIVATE_CONTACT_FAILED:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName={ErrorIcon} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="convert-private_contact-failed-message">Failed converting private contact.</Trans>
            </BannerContentTitle>
            {getTryAgainLocalized()}
          </BannerContent>
        </Banner>
      );
    case ContactDetailsErrorTypesEnum.HANDLE_TAKEN:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName={ErrorIcon} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="contact-handle-already-taken-title">
                <ContactSourceDisplayName contactUid={state.params?.conflictingContactUid} shouldCapitalize />{' '}
                already taken.
              </Trans>
            </BannerContentTitle>
            <Trans id="contact-handle-already-taken-description">
              We could not save this{' '}
              <ContactSourceDisplayName
                contactUid={state.params?.conflictingContactUid}
                shouldCapitalize={false}
              />{' '}
              because it is already taken by another contact.
            </Trans>
            <StyledButtonsContainerDiv>
              <StyledButton
                color={theme.greys.white}
                hoverColor={theme.greys.white}
                onClick={openContactDetailsPopUp}
              >
                <Trans id="contact-details-error-banner-view">View Contact</Trans>
              </StyledButton>
              <StyledButton color={theme.greys.white} hoverColor={theme.greys.white} onClick={onMergePreview}>
                <Trans id="contact-details-error-banner-merge">Merge</Trans>
              </StyledButton>
            </StyledButtonsContainerDiv>
          </BannerContent>
        </Banner>
      );
    case ContactDetailsErrorTypesEnum.TOO_MANY_HANDLES:
      return (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName={ErrorIcon} />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="contact-too-many-handles-title">Contact update failed.</Trans>
            </BannerContentTitle>
            <Trans id="contact-too-many-handles-description">
              A contact cannot have more than 100 handles.
            </Trans>
          </BannerContent>
        </Banner>
      );
    default:
      throw new Error('This should not happen');
  }
};

const getTryAgainLocalized = () => (
  <Trans id="contact-merge-preview-failed-please-try-again-message">Please try again.</Trans>
);
